import { useEffect } from "react";
import { Outlet, useHref, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Eye } from "@untitledui/icons";
import type { PartnerPanelStatus } from "@mfa-aff/shared";
import { AppProviders } from "@mfa-aff/ui";
import { panelStatusKey, usePanelStatus } from "../api/panel";
import { onPanelClosed } from "../api/panel-signal";
import { PanelClosedPage } from "../pages/closed/PanelClosedPage";

/**
 * Root of every panel route: asks `GET /status` first. Closed by the owner (and not an allowlisted
 * preview session) → the «در حال آماده‌سازی» screen instead of anything else, sign-in included.
 * A 503 `partner_panel_closed` from any call flips to the same screen. A preview session sees the
 * panel with a thin banner on top. If the status call itself fails, the panel renders normally
 * (the API still enforces the switch).
 */
export const PanelGate = () => {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const status = usePanelStatus();

    useEffect(
        () =>
            onPanelClosed((message) => {
                qc.setQueryData<PartnerPanelStatus>(panelStatusKey, { open: false, message, preview: false });
            }),
        [qc],
    );

    if (status.isPending) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-primary" role="status" aria-label="در حال بارگذاری">
                <span className="size-8 animate-spin rounded-full border-2 border-brand-solid border-t-transparent" />
            </div>
        );
    }

    const s = status.data;
    if (s && !s.open && !s.preview) {
        return (
            <AppProviders navigate={navigate} useHref={useHref}>
                <PanelClosedPage message={s.message} />
            </AppProviders>
        );
    }

    return (
        <>
            {/* An admin's read-only view has its own bar (ImpersonationBar), which mentions the closed panel. */}
            {s && !s.open && s.preview && !s.impersonation && (
                <div
                    role="status"
                    className="relative z-20 flex items-center justify-center gap-2 bg-warning-solid px-4 py-1.5 text-center text-xs font-medium text-white sm:text-sm lg:ps-[296px]"
                >
                    <Eye className="size-4 shrink-0" aria-hidden="true" />
                    <span>پنل برای بازاریاب‌ها بسته است؛ شما در حالت پیش‌نمایش هستید.</span>
                </div>
            )}
            <Outlet />
        </>
    );
};
